import { ALARM_PLAYBACK_CONFIG } from 'config/alarmPlayback.config';
import { useLiveAlarmUpdates } from 'hooks/useAlarmEvents';
import useTextToSpeech from 'hooks/useTextToSpeech';
import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import type { Alarm } from '../../features/Alarms/types/Alarms.types';

import type alarmVolumeReducer from '../../features/Alarms/slices/alarmsSlice';
import {
  getAlarmAnnouncementKey,
  isAlarmActive,
  isAlarmExpired,
  isSilenced,
  toAnnouncementText,
} from '../../features/Alarms/utils/alarmUtils';
import { useGetAlarmsQuery } from './Alarms.api';

type AlarmVolumeSliceState = ReturnType<typeof alarmVolumeReducer>;

const EMPTY_ALARMS: Alarm[] = [];
const ANNOUNCED_ALARM_KEYS = new Set<string>();
const ACTIVE_ALARM_KEYS_STORAGE_KEY = 'fusion.activeAlarmAnnouncementKeys';

export default function AlarmListener() {
  const tts = useTextToSpeech();
  // Get alarm volume from Redux (float 0.3-1.0)
  const alarmVolume = useSelector(
    (state: { alarmVolume: AlarmVolumeSliceState }) => state.alarmVolume.alarmVolume,
  );
  // only fetch non-silenced alarms
  const { data: alarmQuery, refetch } = useGetAlarmsQuery({
    state: 1,
    limit: 50,
    offset: 0,
    silenced: 0,
  });
  const alarmList = alarmQuery?.data || EMPTY_ALARMS;
  const announcedAlarmKeys = useRef(ANNOUNCED_ALARM_KEYS);
  const alarmsRef = useRef(alarmList);
  const alarmVolumeRef = useRef(alarmVolume);
  const activeAlarmEntriesRef = useRef<{ key: string; alarm: Alarm }[]>([]);
  const previousActiveKeysRef = useRef(new Set<string>());
  const hasHandledInitialSnapshotRef = useRef(false);
  const loopIntervalIdRef = useRef<number | null>(null);
  const restoredFromSessionRef = useRef(false);

  if (!restoredFromSessionRef.current && typeof window !== 'undefined') {
    restoredFromSessionRef.current = true;
    try {
      const rawStoredKeys = window.sessionStorage.getItem(ACTIVE_ALARM_KEYS_STORAGE_KEY);
      const storedKeys = JSON.parse(rawStoredKeys || '[]');
      if (Array.isArray(storedKeys) && storedKeys.length > 0) {
        const restoredKeys = new Set<string>(storedKeys.map((key: string) => String(key)));
        previousActiveKeysRef.current = restoredKeys;
        announcedAlarmKeys.current = new Set(restoredKeys);
      }
    } catch (error) {
      console.warn(
        '[AlarmListenerV2] Failed to restore active alarm keys from session storage',
        error,
      );
    }
  }

  alarmsRef.current = alarmList;
  alarmVolumeRef.current = alarmVolume;
  useLiveAlarmUpdates(refetch);

  const clearLoop = useCallback(() => {
    if (loopIntervalIdRef.current !== null) {
      window.clearInterval(loopIntervalIdRef.current);
      loopIntervalIdRef.current = null;
    }
  }, []);

  const speakAlarms = useCallback(
    (entries: { key: string; alarm: Alarm }[], forceInterrupt = false) => {
      if (!entries.length) return false;
      const shouldWaitForSpeech =
        ALARM_PLAYBACK_CONFIG.skipWhenSpeechBusy && (tts.isSpeaking() || tts.isPending());
      if (shouldWaitForSpeech) return false;

      // Build the list of Utterances to speak
      const speakList: { text: string; repeat: boolean }[] = [];
      if (ALARM_PLAYBACK_CONFIG.headingText) {
        speakList.push({ text: ALARM_PLAYBACK_CONFIG.headingText, repeat: false });
      }
      const maxMessages = Math.max(1, ALARM_PLAYBACK_CONFIG.maxMessagesPerCycle);
      const limitedEntries = entries.slice(0, maxMessages);
      for (const { key, alarm } of limitedEntries) {
        announcedAlarmKeys.current.add(key);
        speakList.push({ text: toAnnouncementText(alarm), repeat: false });
      }
      if (speakList.length === 0) return false;

      // Use ref so the loop interval always reads the latest volume, not a stale closure value
      const getVolume = () => Math.max(0.3, Math.min(1.0, alarmVolumeRef.current));
      const volForSpeak = getVolume();
      tts.beginNewQueue(
        speakList,
        false,
        forceInterrupt || ALARM_PLAYBACK_CONFIG.interruptCurrentSpeech,
        volForSpeak,
        0,
        getVolume,
      );
      return true;
    },
    [tts],
  );
  // Immediate restart of TTS at new volume if volume changes while speaking
  useEffect(() => {
    if (tts.isSpeaking()) {
      tts.cancel();
      const activeEntries = activeAlarmEntriesRef.current;
      if (activeEntries.length > 0) {
        // Resume from the last spoken index
        const currentIdx = tts.getCurrentIndex ? tts.getCurrentIndex() : 0;
        // Remove the buffer (first item is '') if present
        const offset = currentIdx > 0 ? currentIdx - 1 : 0;
        speakAlarms(activeEntries.slice(offset), true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alarmVolume]);

  const runLoopCycle = useCallback(() => {
    const latestAlarms = alarmsRef.current;

    const activeEntries = activeAlarmEntriesRef.current.filter(({ key }) => {
      const latest = latestAlarms.find((a) => getAlarmAnnouncementKey(a) === key);
      const isActive = latest && isAlarmActive(latest);
      const notSilenced = latest && !isSilenced(latest);
      const notExpired = latest && !isAlarmExpired(latest);
      return latest && isActive && notSilenced && notExpired;
    });

    if (!activeEntries.length) return;
    // Announce all active alarms every cycle
    speakAlarms(activeEntries);
  }, [speakAlarms]);

  useEffect(() => {
    //No need for silence filtering here, we get only non silenced from api

    // Deduping logic based on announcement keys
    const dedupedActiveAlarms: { key: string; alarm: Alarm }[] = [];
    const activeKeys = new Set<string>();
    for (const alarm of alarmList) {
      const key = getAlarmAnnouncementKey(alarm);
      if (!activeKeys.has(key)) {
        activeKeys.add(key);
        dedupedActiveAlarms.push({ key, alarm });
      }
    }
    for (const key of announcedAlarmKeys.current) {
      if (!activeKeys.has(key)) {
        announcedAlarmKeys.current.delete(key);
      }
    }

    activeAlarmEntriesRef.current = dedupedActiveAlarms;
    const previousActiveKeys = previousActiveKeysRef.current;
    const newlyActiveEntries = dedupedActiveAlarms.filter(
      ({ key }) => !previousActiveKeys.has(key),
    );
    const isInitialSnapshot = !hasHandledInitialSnapshotRef.current;
    hasHandledInitialSnapshotRef.current = true;
    if (dedupedActiveAlarms.length === 0) {
      if (typeof window !== 'undefined') {
        try {
          window.sessionStorage.removeItem(ACTIVE_ALARM_KEYS_STORAGE_KEY);
        } catch (error) {
          console.warn(
            '[AlarmListenerV2] Failed to clear active alarm keys from session storage',
            error,
          );
        }
      }
      previousActiveKeysRef.current = activeKeys;
      clearLoop();
      return;
    }
    if (typeof window !== 'undefined') {
      try {
        window.sessionStorage.setItem(
          ACTIVE_ALARM_KEYS_STORAGE_KEY,
          JSON.stringify(Array.from(activeKeys)),
        );
      } catch (error) {
        console.warn(
          '[AlarmListenerV2] Failed to persist active alarm keys to session storage',
          error,
        );
      }
    }
    const restoredInitialSnapshot =
      isInitialSnapshot && previousActiveKeys.size > 0 && newlyActiveEntries.length === 0;
    const shouldAnnounceOnRefresh =
      isInitialSnapshot && ALARM_PLAYBACK_CONFIG.announceOnRefresh && !restoredInitialSnapshot;
    const shouldReplayUnsilenced =
      !isInitialSnapshot &&
      ALARM_PLAYBACK_CONFIG.replayOnUnsilence &&
      newlyActiveEntries.length > 0;
    if (shouldAnnounceOnRefresh) {
      speakAlarms(dedupedActiveAlarms);
    } else if (shouldReplayUnsilenced) {
      speakAlarms(newlyActiveEntries);
    }
    if (
      ALARM_PLAYBACK_CONFIG.loopEnabled &&
      ALARM_PLAYBACK_CONFIG.loopIntervalMs > 0 &&
      loopIntervalIdRef.current === null
    ) {
      loopIntervalIdRef.current = window.setInterval(
        runLoopCycle,
        ALARM_PLAYBACK_CONFIG.loopIntervalMs,
      );
    }
    previousActiveKeysRef.current = activeKeys;
  }, [alarmList, clearLoop, runLoopCycle, speakAlarms]);

  useEffect(() => {
    return () => {
      clearLoop();
    };
  }, [clearLoop]);

  return null;
}
